import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import SettingMenu from './settingMenu';
interface PasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const SecurityComponent: React.FC = () => {
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm<PasswordData>();

  const onSubmit = (data: PasswordData) => {
    console.log('Password change data:', data);
    reset();
  };

  const handleReset = () => {
    reset();
  };

  const handleEnableTwoFactor = () => {
    setTwoFactorEnabled(true);
    console.log('Enable two-factor authentication');
  };

  // Password strength validation
  const newPassword = watch('newPassword');
  const hasMinLength = newPassword && newPassword.length >= 8;
  const hasLowercase = newPassword && /[a-z]/.test(newPassword);
  const hasNumberOrSymbol = newPassword && /[0-9!@#$%^&*(),.?":{}|<>\s]/.test(newPassword);

  return (
    <div>
      <SettingMenu />
      <div className="bg-white rounded-md shadow-lg p-6 mx-auto space-y-8">
        {/* Change Password Section */}
        <div>
          <h2 className="text-xl font-semibold text-gray-800 mb-6">Change Password</h2>

          <div>
            <div className="space-y-4">
              {/* Current Password */}
              <div className="form-group">
                <label className="block mb-1 font-medium text-gray-700">Current Password</label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    className="input-style pr-20"
                    placeholder="············"
                    {...register('currentPassword', {
                      required: true
                    })}
                  />
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                    <div className="w-6 h-6 bg-red-100 rounded flex items-center justify-center">
                      <div className="w-3 h-3 bg-red-500 rounded-sm flex items-center justify-center">
                        <span className="text-white text-xs">!</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="text-gray-400 hover:text-gray-600">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24">
                        {showCurrentPassword ? (
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                          />
                        ) : (
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        )}
                      </svg>
                    </button>
                  </div>
                </div>
                {errors.currentPassword && (
                  <p className="text-red-500 text-sm mt-1">Current password is required</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* New Password */}
                <div className="form-group">
                  <label className="block mb-1 font-medium text-gray-700">New Password</label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      className="input-style pr-20"
                      placeholder="············"
                      {...register('newPassword', {
                        required: true,
                        minLength: 8,
                        validate: {
                          hasLowercase: (value) =>
                            /[a-z]/.test(value) || 'Must contain lowercase letter',
                          hasNumberOrSymbol: (value) =>
                            /[0-9!@#$%^&*(),.?":{}|<>\s]/.test(value) ||
                            'Must contain number, symbol, or whitespace'
                        }
                      })}
                    />
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                      <div className="w-6 h-6 bg-red-100 rounded flex items-center justify-center">
                        <div className="w-3 h-3 bg-red-500 rounded-sm flex items-center justify-center">
                          <span className="text-white text-xs">!</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="text-gray-400 hover:text-gray-600">
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24">
                          {showNewPassword ? (
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                            />
                          ) : (
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                          )}
                        </svg>
                      </button>
                    </div>
                  </div>
                  {errors.newPassword && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.newPassword.message || 'New password is required'}
                    </p>
                  )}
                </div>

                {/* Confirm New Password */}
                <div className="form-group">
                  <label className="block mb-1 font-medium text-gray-700">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      className="input-style pr-12"
                      placeholder="············"
                      {...register('confirmPassword', {
                        required: true,
                        validate: (value) => {
                          if (watch('newPassword') !== value) {
                            return "Passwords don't match";
                          }
                        }
                      })}
                    />
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                      <div className="w-6 h-6 bg-red-100 rounded flex items-center justify-center">
                        <div className="w-3 h-3 bg-red-500 rounded-sm flex items-center justify-center">
                          <span className="text-white text-xs">!</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="text-gray-400 hover:text-gray-600">
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24">
                          {showConfirmPassword ? (
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                            />
                          ) : (
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                          )}
                        </svg>
                      </button>
                    </div>
                  </div>
                  {errors.confirmPassword && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.confirmPassword.message || 'Please confirm your password'}
                    </p>
                  )}
                </div>
              </div>

              {/* Password Requirements */}
              <div className="bg-gray-50 p-4 rounded-md">
                <h4 className="font-medium text-gray-700 mb-3">Password Requirements:</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li
                    className={`flex items-center gap-2 ${
                      hasMinLength ? 'text-green-600' : 'text-gray-600'
                    }`}>
                    <span
                      className={`w-2 h-2 rounded-full ${
                        hasMinLength ? 'bg-green-500' : 'bg-gray-400'
                      }`}></span>
                    Minimum 8 characters long - the more, the better
                  </li>
                  <li
                    className={`flex items-center gap-2 ${
                      hasLowercase ? 'text-green-600' : 'text-gray-600'
                    }`}>
                    <span
                      className={`w-2 h-2 rounded-full ${
                        hasLowercase ? 'bg-green-500' : 'bg-gray-400'
                      }`}></span>
                    At least one lowercase character
                  </li>
                  <li
                    className={`flex items-center gap-2 ${
                      hasNumberOrSymbol ? 'text-green-600' : 'text-gray-600'
                    }`}>
                    <span
                      className={`w-2 h-2 rounded-full ${
                        hasNumberOrSymbol ? 'bg-green-500' : 'bg-gray-400'
                      }`}></span>
                    At least one number, symbol, or whitespace character
                  </li>
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-shrink-0 flex-wrap items-center rounded-b-md">
                <button
                  type="button"
                  onClick={handleSubmit(onSubmit)}
                  className="px-6 btn-primary w-32 mr-2 ml-0 mt-0">
                  Save
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors text-sm font-medium">
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Two-Step Verification Section */}
        <div className="border-t pt-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Two-steps verification</h2>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-medium text-gray-700 mb-2">
                Two factor authentication is not enabled yet.
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                Two-factor authentication adds an additional layer of security to your account by
                requiring more than just a password to log in.{' '}
                <a href="#" className="text-blue-600 hover:text-blue-800 underline">
                  Learn more.
                </a>
              </p>
            </div>

            <div>
              <button
                type="button"
                onClick={handleEnableTwoFactor}
                className="px-6 btn-primary w-96 mr-2 ml-0 mt-0">
                Enable Two-Factor Authentication
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityComponent;
